import type { Road, RoadClass, ZoneName } from '../content/types';
import type { Fields } from './fields';
import { hash01, hashStr, Rng } from './rng';
import { PIECE } from './overlay';

/**
 * Road network as a global function. Roads are straight lines on a hierarchical grid:
 * level 0 lines every 128 tiles, level 1 the odd multiples of 64, level 2 of 32,
 * level 3 of 16. A line exists in a 128-tile segment if the zone there is dense enough
 * for that level (zone.blockSize <= spacing) and, for the zone's finest level, a hash
 * passes zone.roadChance. Everything is keyed by integers, so any chunk computes the
 * same answer without talking to its neighbours.
 */
export const SPACING = [128, 64, 32, 16] as const;
export const SEG = SPACING[0];
/** overpass geometry: deck height, flat tiles beyond the crossed road's edge, ramp length, share of crossings that get one */
export const OVER_H = 5, OVER_FLAT = 4, OVER_RAMP = 22, OVER_CHANCE = 0.9;
/** the embankment beside a ramp: this many tiles of bank per metre of height, either side of the road (the renderer's skirt and the generator's blockH agree on it) */
export const BANK = 0.7;
/** (bug report, Sep 22) the bank beside a ramp stops where it is lower than this: a car mounts a slope that low (the generator's bank is solid only above it) */
export const BANK_TOE = 0.6;
/** the steepest ramp any deck climbs, metres a tile: what a vehicle's corner probes allow for (physics.ts `blockedPoints`) and the shortest ramp the path world lays (path-adapter.ts `RAMP_MIN`) */
export const RAMP_GRADE = 0.45;
/** frontage (slip) roads beside an overpass: lanes this wide on both sides of the highway, from the crossed road to past the ramp foot */
export const FRONTAGE_W = 3;
/** an ending line reaches at most this far past its segment boundary: half the widest road's total width (a boulevard with sidewalks is 12) */
export const HALF_MAX = 8;
export type Axis = 'v' | 'h';

/** Which road classes each level may use (first match with zone weight wins). */
const LEVEL_CLASSES: RoadClass[][] = [
  ['highway', 'avenue'],
  ['avenue', 'street'],
  ['street', 'lane'],
  ['lane', 'street', 'dirt'],
];

export interface LineRef { axis: Axis; level: number; k: number; seg: number }
export interface RoadHit { road: Road; axis: Axis; level: number; k: number; pos: number; /** offset of tile from line centre, tiles */ off: number; /** (track 2) a player-built piece (overlay.ts), not a generated road — growth's `generatedAt` seam */ built?: true; /** (track 2) the deck height under this hit on a path edge — a bridge over water or a raised crossing; the lattice never sets it (`overpassH` is its geometry) */ deck?: number; /** (path world) a deck tile the stroke only touches: outside the rounded width, inside the painted one — a floor with no railing and no pillar, so a car in the outer lane is not thrown against a stepped parapet (owner, Sep 11) */ ledge?: true; /** (path world, Sep 17) the edge's exact centre line across this row, tile units — `pos` is it rounded; a driver's lane is measured from this, or on a stroke that runs between two columns the outer lane sits half a tile into the parapet */ centre?: number }

export class RoadNet {
  private cache = new Map<string, Road | null>();

  constructor(readonly fields: Fields) {}

  /** World coordinate of line k at a level. */
  static linePos(level: number, k: number): number { return k * SPACING[level]; }

  /** Is (level,k) a line that is *new* at this level (not already a coarser line)? */
  static isOwnLevel(level: number, k: number): boolean { return level === 0 || (k & 1) === 1; }

  finestLevel(zone: ZoneName): number {
    const bs = this.fields.zone(zone).blockSize;
    let f = 0;
    for (let L = 0; L < SPACING.length; L++) if (SPACING[L] >= bs) f = L;
    return f;
  }

  /** Road definition for a line segment, or null if it does not exist. Cached. */
  lineDef(axis: Axis, level: number, k: number, seg: number): Road | null {
    if (!RoadNet.isOwnLevel(level, k)) return null;
    const key = `${axis}${level}:${k}:${seg}`;
    const c = this.cache.get(key);
    if (c !== undefined) return c;
    if (this.cache.size > 200000) this.cache.clear();
    const r = this.computeLine(axis, level, k, seg);
    this.cache.set(key, r);
    return r;
  }

  /**
   * A finer-level segment only exists if both of its ends lead somewhere: a level-0 line crossing
   * at the segment boundary, or the same line carrying on (which must itself lead somewhere within
   * a few segments). Without this, streets from a town's edge ran 128 tiles into the forest and
   * stopped dead. Level-0 lines (avenues/highways) are long enough to be left alone.
   */
  private computeLine(axis: Axis, level: number, k: number, seg: number): Road | null {
    const raw = this.rawLine(axis, level, k, seg);
    if (!raw || level === 0 || raw.class === 'rail') return raw;
    if (!this.endLeadsSomewhere(axis, level, k, seg, -1, 0) || !this.endLeadsSomewhere(axis, level, k, seg, 1, 0)) return null;
    return raw;
  }

  /** Does the end of segment `seg` in direction `dir` (−1: start, +1: end) connect to anything? */
  private endLeadsSomewhere(axis: Axis, level: number, k: number, seg: number, dir: 1 | -1, depth: number): boolean {
    const boundary = dir < 0 ? seg : seg + 1; // multiples of SEG: only level-0 lines of the other axis cross exactly here
    const pos = RoadNet.linePos(level, k);
    const perp: Axis = axis === 'v' ? 'h' : 'v';
    if (this.rawLine(perp, 0, boundary, Math.floor(pos / SEG))) return true;
    const next = seg + dir;
    if (!this.rawLine(axis, level, k, next)) return false;
    return depth >= 3 || this.endLeadsSomewhere(axis, level, k, next, dir, depth + 1);
  }

  private rawCache = new Map<string, Road | null>();
  private rawLine(axis: Axis, level: number, k: number, seg: number): Road | null {
    if (!RoadNet.isOwnLevel(level, k)) return null;
    const key = `${axis}${level}:${k}:${seg}`;
    const c = this.rawCache.get(key);
    if (c !== undefined) return c;
    if (this.rawCache.size > 200000) this.rawCache.clear();
    const r = this.computeRaw(axis, level, k, seg);
    this.rawCache.set(key, r);
    return r;
  }

  private computeRaw(axis: Axis, level: number, k: number, seg: number): Road | null {
    const pos = RoadNet.linePos(level, k);
    const mid = (seg + 0.5) * SEG;
    const [x, y] = axis === 'v' ? [pos, mid] : [mid, pos];
    // a railway takes a level-1 line beside a corridor, town or wilderness alike (it replaces the street that would be there)
    if (level === 1 && this.fields.settlements.onRail(axis, pos, seg)) { const rail = this.fields.reg.all('road').find((r) => r.class === 'rail'); if (rail) return rail; }
    const zone = this.fields.urbanZone(x, y);
    const z = this.fields.zone(zone);
    if (z.blockSize > SPACING[level]) return null;
    // deep wilderness: only highway corridors between towns
    if (zone === 'rural' && this.fields.density(x, y) < 0.02) {
      if (level !== 0 || !this.fields.settlements.onCorridor(axis, pos, seg)) return null;
      const hw = this.fields.reg.all('road').filter((r) => r.class === 'highway');
      if (hw.length) return hw[0];
    }
    const finest = this.finestLevel(zone);
    const h = hashStr(`line:${axis}:${level}:${k}:${seg}`, this.fields.seed);
    if (level === finest && hash01(h, 1) >= z.roadChance) return null;
    // pick a road def: first class in the level list that has any road weighted for this zone
    const reg = this.fields.reg;
    const rng = new Rng(h);
    const classes = LEVEL_CLASSES[level];
    const cands = reg.all('road').filter((r) => classes.includes(r.class) && (r.zones[zone] ?? 0) > 0);
    if (cands.length) return rng.weighted(cands, (r) => r.zones[zone] ?? 0) ?? cands[0];
    // fallback: any road of the level's classes regardless of zone
    for (const cls of LEVEL_CLASSES[level]) {
      const cands = reg.all('road').filter((r) => r.class === cls);
      if (cands.length) return cands[0];
    }
    return null;
  }

  /**
   * The intersection piece (owner: "always have an intersection piece of road to which the two or more roads
   * connect"): a line that ends at a segment boundary ends on a crossing road — segments are SEG long, so only a
   * level-0 line of the other axis crosses there — and it used to stop at that road's centre line, leaving a notch
   * of grass in the outer corner of an L and a crosswalk that reached only halfway across a T. Now the ending
   * line carries on across the whole crossing road (its width plus sidewalks, the wider of the two segments it may
   * have there), so every tile of the crossing is on both roads at once. Returns the road of the ending line when
   * tile `u` of the empty segment `seg` lies within that crossing, else null.
   */
  private extension(axis: Axis, level: number, k: number, seg: number, pos: number, u: number): Road | null {
    const start = seg * SEG, dir: 1 | -1 = u - start < SEG / 2 ? -1 : 1; // the nearer boundary of this (empty) segment
    const b = dir < 0 ? start : start + SEG;
    if (Math.abs(u - b) > HALF_MAX) return null;
    const road = this.lineDef(axis, level, k, seg + dir);
    if (!road || road.class === 'rail') return null;
    const perp: Axis = axis === 'v' ? 'h' : 'v', ps = Math.floor(pos / SEG);
    let w = 0;
    for (const r of [this.lineDef(perp, 0, b / SEG, ps), this.lineDef(perp, 0, b / SEG, ps - 1)]) if (r && r.class !== 'rail') w = Math.max(w, r.width + 2 * (r.sidewalk ?? 0));
    return w && RoadNet.covers(b, w, u) ? road : null;
  }

  /** Tile → tiles covered: a road of width w centred on `pos` covers [pos - floor((w-1)/2), pos + floor(w/2)]. */
  static covers(pos: number, width: number, t: number): boolean {
    return t >= pos - Math.floor((width - 1) / 2) && t <= pos + Math.floor(width / 2);
  }

  /** Lines of one axis that cover coordinate `t` (perpendicular coord `u` selects the segment). */
  private hitsAlong(axis: Axis, t: number, u: number, out: RoadHit[]): void {
    const seg = Math.floor(u / SEG);
    for (let L = 0; L < SPACING.length; L++) {
      const sp = SPACING[L];
      const k = Math.round(t / sp);
      if (!RoadNet.isOwnLevel(L, k)) continue;
      const pos = k * sp;
      const road = this.lineDef(axis, L, k, seg) ?? this.extension(axis, L, k, seg, pos, u);
      if (!road) continue;
      const total = road.width + 2 * (road.sidewalk ?? 0);
      if (RoadNet.covers(pos, total, t)) out.push({ road, axis, level: L, k, pos, off: t - pos });
    }
    // player-built pieces (overlay.ts): a finished 16-tile stretch of a lattice line nobody generated
    const ov = this.fields.overlay;
    if (ov.roads.size) {
      const pos = Math.round(t / PIECE) * PIECE;
      const piece = ov.builtAt(axis, pos, u);
      if (piece && !out.some((h) => h.axis === axis && h.pos === pos)) {
        const road = this.fields.reg.get('road', piece.r);
        if (road && RoadNet.covers(pos, road.width + 2 * (road.sidewalk ?? 0), t)) out.push({ road, axis, level: 3, k: pos / PIECE, pos, off: t - pos, built: true });
      }
    }
  }

  /**
   * Overpasses: where a level-0 highway crosses another level-0 road (avenue/boulevard, or a
   * second highway — then the vertical one goes over), the highway rises on a deck: flat over
   * the crossing (the other road's width + 2·OVER_FLAT), ramps of OVER_RAMP tiles either side.
   * A hashed OVER_CHANCE of crossings stay at grade. Returns the deck height (0 = none) of the
   * given highway hit at world tile (x, y).
   */
  overpassH(h: RoadHit, x: number, y: number): number {
    if (h.level !== 0 || h.road.class !== 'highway') return 0;
    const along = h.axis === 'v' ? y : x;                      // coordinate along the highway
    const kx = Math.round(along / SEG); const cross = kx * SEG;   // nearest level-0 crossing position
    const dist = Math.abs(along - cross);
    if (dist > OVER_FLAT + OVER_RAMP + 8) return 0;
    const perp: Axis = h.axis === 'v' ? 'h' : 'v';
    const segOfPos = Math.floor(h.pos / SEG);
    // the crossed road must run through (both sides), else it would dead-end under the pillars
    const other = this.rawLine(perp, 0, kx, segOfPos), other2 = this.rawLine(perp, 0, kx, segOfPos - 1);
    if (!other || !other2 || other.class === 'dirt' || other.class === 'lane') return 0;
    if (other.class === 'highway' && h.axis === 'h') return 0; // two highways: the vertical one goes over
    // the highway must carry on through the crossing (both segments)
    if (this.rawLine(h.axis, 0, h.k, kx)?.class !== 'highway' || this.rawLine(h.axis, 0, h.k, kx - 1)?.class !== 'highway') return 0; // a highway on both sides of the crossing
    if (hash01(hashStr(`over:${h.axis}:${h.k}:${kx}`, this.fields.seed), 3) >= OVER_CHANCE) return 0;
    const flat = Math.floor((other.width + 2 * (other.sidewalk ?? 0)) / 2) + OVER_FLAT;
    if (dist <= flat) return OVER_H;
    const t = (dist - flat) / OVER_RAMP;
    return t >= 1 ? 0 : Math.round(OVER_H * (1 - t) * 100) / 100;
  }

  /** Is there an overpass at the level-0 crossing (kx along the highway line `axis`,k)? */
  overpassAt(axis: Axis, k: number, kx: number): boolean {
    const road = this.rawLine(axis, 0, k, kx);
    if (!road || road.class !== 'highway') return false;
    const pos = k * SEG, cross = kx * SEG;
    const [x, y] = axis === 'v' ? [pos, cross] : [cross, pos];
    return this.overpassH({ road, axis, level: 0, k, pos, off: 0 }, x, y) > 0;
  }

  /** Width a line takes up for lot carving: road + sidewalks, plus frontage lanes along a highway segment that ends at an overpass. */
  laneWidth(axis: Axis, level: number, k: number, seg: number, road: Road): number {
    const w = road.width + 2 * (road.sidewalk ?? 0);
    if (level !== 0 || road.class !== 'highway') return w;
    return this.overpassAt(axis, k, seg) || this.overpassAt(axis, k, seg + 1) ? w + 2 * FRONTAGE_W : w;
  }

  /**
   * Frontage road at a ground tile: an interchange's slip lanes run beside the highway from the
   * crossed road's edge to just past the ramp foot, on both sides and in both directions, so a car
   * on the avenue can get up onto the highway (and off it). Returns the lane's axis, or null.
   */
  frontageAt(x: number, y: number): { axis: Axis; road: Road } | null {
    for (const axis of ['v', 'h'] as const) {
      const t = axis === 'v' ? x : y, u = axis === 'v' ? y : x;
      const k = Math.round(t / SEG), pos = k * SEG;
      const seg = Math.floor(u / SEG);
      const road = this.rawLine(axis, 0, k, seg);
      if (!road || road.class !== 'highway') continue;
      const total = road.width + 2 * (road.sidewalk ?? 0);
      const beyond = t >= pos ? t - (pos + Math.floor(total / 2)) : (pos - Math.floor((total - 1) / 2)) - t;
      if (beyond < 1 || beyond > FRONTAGE_W) continue;
      const kx = Math.round(u / SEG), cross = kx * SEG;
      if (!this.overpassAt(axis, k, kx)) continue;
      const perp: Axis = axis === 'v' ? 'h' : 'v';
      const other = this.rawLine(perp, 0, kx, Math.floor(pos / SEG)); if (!other) continue;
      const otherHalf = Math.floor((other.width + 2 * (other.sidewalk ?? 0)) / 2);
      const flat = otherHalf + OVER_FLAT;
      const dist = Math.abs(u - cross);
      if (dist <= Math.floor(other.width / 2) || dist > flat + OVER_RAMP + 3) continue; // from the crossed road's kerb (through its sidewalk) to past the ramp foot
      const t2 = this.fields.terrainAt(x, y);
      if (!t2.walkable && !t2.buildable) continue; // no slip road over water
      return { axis, road };
    }
    return null;
  }

  /** All road lines covering tile (x,y), coarsest first. Empty if none. */
  hitsAt(x: number, y: number, out: RoadHit[] = []): RoadHit[] {
    out.length = 0;
    this.hitsAlong('v', x, y, out);
    this.hitsAlong('h', y, x, out);
    out.sort((a, b) => a.level - b.level);
    return out;
  }

  /** Does any existing line of `axis` pass exactly through coordinate t (any level), in segment of u? */
  lineAt(axis: Axis, t: number, u: number): { level: number; k: number; road: Road; /** a player-built piece: the stretch it covers along the line */ span?: [number, number] } | null {
    if (t % SPACING[3] !== 0) return null;
    const seg = Math.floor(u / SEG);
    for (let L = 0; L < SPACING.length; L++) {
      if (t % SPACING[L] !== 0) continue;
      const k = t / SPACING[L];
      if (!RoadNet.isOwnLevel(L, k)) continue;
      const road = this.lineDef(axis, L, k, seg);
      if (road) return { level: L, k, road };
      break;
    }
    const piece = this.fields.overlay.roads.size ? this.fields.overlay.builtAt(axis, t, u) : undefined;
    if (piece) { const road = this.fields.reg.get('road', piece.r); if (road) return { level: 3, k: t / PIECE, road, span: [piece.f, piece.f + PIECE] }; }
    return null;
  }
  /** Is there a generated line (any level) at lattice coordinate t in the segment of u? (planners may not plan over one) */
  generatedAt(axis: Axis, t: number, u: number): boolean {
    if (t % SPACING[3] !== 0) return false;
    const seg = Math.floor(u / SEG);
    for (let L = 0; L < SPACING.length; L++) {
      if (t % SPACING[L] !== 0) continue;
      const k = t / SPACING[L];
      if (!RoadNet.isOwnLevel(L, k)) continue;
      return !!this.lineDef(axis, L, k, seg);
    }
    return false;
  }

  /** Crossings of perpendicular lines along a line, within [from, to). Sorted. */
  crossings(axis: Axis, pos: number, from: number, to: number): { at: number; road: Road }[] {
    const perp: Axis = axis === 'v' ? 'h' : 'v';
    const out: { at: number; road: Road }[] = [];
    const step = SPACING[3];
    for (let t = Math.ceil(from / step) * step; t < to; t += step) {
      const l = this.lineAt(perp, t, pos);
      if (l) out.push({ at: t, road: l.road });
    }
    return out;
  }

  /** Distance from a line at `pos` to the nearest parallel existing line in direction dir (±1), max 128. */
  gapToParallel(axis: Axis, pos: number, u: number, dir: 1 | -1): number {
    const step = SPACING[3];
    for (let i = 1; i <= SEG / step; i++) {
      const t = pos + dir * i * step;
      if (this.lineAt(axis, t, u)) return i * step;
    }
    return SEG;
  }
}
